import type { DeviceType } from '@renderer/types/utils'

type CameraKey = 'primary' | 'secondary'

/**
 * Which device object is behind a preview slot right now.
 *
 * The live preview measures its crop rectangle ONCE, from a camera frame, and reuses it for
 * every frame after that — re-measuring per frame would mean a second full decode of every
 * blob. That cache is only valid for the device it was measured from, so the loop needs a
 * cheap, honest answer to "is this still the same camera?".
 *
 * Both handles are part of the answer, not just the one the label points at: the DSLR and
 * webcam handles are nulled independently (the webcam branch of `tryToConnect` leaves a
 * previous DSLR handle in place), so a single-handle stamp can miss a real switch. Any
 * reference change means a connect replaced the object, which is exactly when the cached
 * crop rect has to go.
 */
export interface PreviewDeviceStamp {
  camera: unknown
  webcam: unknown
  deviceType: DeviceType | undefined
}

/** The fields of the camera store this cares about — kept structural so tests need no store. */
export interface PreviewDeviceSource {
  camera: unknown
  webcam: unknown
  deviceType: DeviceType | undefined
  secondaryCamera: unknown
  secondaryWebcam: unknown
  secondaryDeviceType: DeviceType | undefined
}

/** The stamp for one slot. */
export function readPreviewDeviceStamp(
  state: PreviewDeviceSource,
  cameraKey: CameraKey
): PreviewDeviceStamp {
  return cameraKey === 'primary'
    ? { camera: state.camera, webcam: state.webcam, deviceType: state.deviceType }
    : {
        camera: state.secondaryCamera,
        webcam: state.secondaryWebcam,
        deviceType: state.secondaryDeviceType
      }
}

/**
 * Whether a cached measurement taken against `measured` still applies to `current`.
 *
 * `null` — nothing measured yet — is never a match, so the first frame always measures.
 */
export function isSamePreviewDevice(
  measured: PreviewDeviceStamp | null,
  current: PreviewDeviceStamp
): boolean {
  return (
    measured !== null &&
    measured.camera === current.camera &&
    measured.webcam === current.webcam &&
    measured.deviceType === current.deviceType
  )
}
